import {
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import {
  FormattedOwnerData,
  OwnerDataCups,
} from "../../../core/models/ownerData/ownerData.model";
import ConsumptionsManagementsDeliveryAddressComponent from "../../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import { t } from "i18next";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import theme from "../../../muiTheme";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import EmptyStateComponent from "../../UI/EmptyStateComponent/EmptyStateComponent";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import {
  FormattedComparativeConsumptions,
  FormattedConsumptionList,
  GetGraphDataRequestBody,
  SelectedDataComparativeConsumptions,
} from "../../../core/models/consumptions/consumptions.model";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import "./ConsumptionsComparative.scss";
import { getGraphDataComparativeConsumptions } from "../../../core/services/consumptions/consumptions.service";
import { ActiveElement, Chart, ChartEvent } from "chart.js";
import {
  formatComparativeConsumptions,
  getBarPositionX,
  getLineChart,
  handleClickLineChart,
} from "../../../utils/components/consumptions/consumptions.util";
import dayjs from "dayjs";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import SnackbarComponent from "../../UI/SnackbarComponent/SnackbarComponent";
import { getSupplies } from "../../../core/services/supplies/supplies.service";
import { GetSuppliesRequestBody } from "../../../core/models/supplies/supplies.model";

export const DEFAULT_YEARS_TO_COMPARE_CONSUMPTION = {
  firstYear: NaN,
  secondYear: NaN,
};

export const DEFAULT_SELECTED_DATA_PERIOD_COMPARATIVE: SelectedDataComparativeConsumptions =
  {
    consumptions: [],
    positionX: null,
    elements: [],
  };

export interface CompareState {
  selectedYears: typeof DEFAULT_YEARS_TO_COMPARE_CONSUMPTION;
  yearOptions: number[];
  ownerDataCup: OwnerDataCups;
}

interface ConsumptionsComparativeProps {
  ownerDataCups: OwnerDataCups[];
  consumptionsList: FormattedComparativeConsumptions[];
  setConsumptionsList: React.Dispatch<
    React.SetStateAction<FormattedComparativeConsumptions[]>
  >;
  selectedDataPeriod: SelectedDataComparativeConsumptions;
  setSelectedDataPeriod: React.Dispatch<
    React.SetStateAction<SelectedDataComparativeConsumptions>
  >;
  comparisonState: CompareState;
  setComparisonState: React.Dispatch<React.SetStateAction<CompareState>>;
}

const ConsumptionsComparative = ({
  ownerDataCups,
  consumptionsList,
  setConsumptionsList,
  selectedDataPeriod: { elements, consumptions, positionX },
  setSelectedDataPeriod,
  comparisonState,
  setComparisonState,
}: ConsumptionsComparativeProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstChartLoadFlag, setIsFirstChartLoadFlag] =
    useState<boolean>(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chart = useRef<Chart | null>(null);
  const isDesktopViewport = useMediaQuery("(min-width: 1100px )");
  const periods = ["P1", "P2", "P3", "P4", "P5", "P6"];
  const [environment, setEnvironment] = useState<"Gas" | "Luz">("Luz");

  const { setSnackbarData, snackbarData, ownerData, userData } =
    useContext(TotalEnergiesContext);

  const showCommonErrorSnackbar = useCallback(() => {
    setSnackbarData({
      isOpen: true,
      isSuccessSeverity: false,
      text: t(
        "Servicios.Parece que ha habido un error, prueba de nuevo más tarde"
      ),
    });
  }, [setSnackbarData]);

  const [isLoadingYearsOptions, setIsLoadingYearsOptions] = useState(false);

  const getCUPyears = useCallback(
    (
      ownerData: FormattedOwnerData,
      codeCups: string,
      token: string,
      abortSignal: AbortSignal
    ) => {
      setComparisonState((prev) => ({
        ...prev,
        selectedYears: DEFAULT_YEARS_TO_COMPARE_CONSUMPTION,
        yearOptions: [],
      }));
      setIsLoadingYearsOptions(true);
      const getSuppliesRequestBody: GetSuppliesRequestBody = {
        userid: ownerData.isAdministrator
          ? ownerData.idAdministrator
          : ownerData.idClient,
        isAdmin: ownerData?.isAdministrator,
        environmentCode: null,
        pageSize: "100",
        pageIndex: "0",
        city: null,
        cups: [codeCups],
      };

      getSupplies(getSuppliesRequestBody, token, abortSignal)
        .then(({ cups }) => {
          const yearList = new Set<number>();
          cups.forEach((cup) => {
            const firstYear = dayjs(cup.FechaAlta).year();
            if (cup.FechaBaja === null) {
              yearList.add(firstYear);
              const actualYear = dayjs().year();
              for (let year = firstYear; year <= actualYear; year++) {
                yearList.add(year);
              }
            } else {
              const lastYear = dayjs(cup.FechaBaja).year();
              for (let year = firstYear; year <= lastYear; year++) {
                yearList.add(year);
              }
            }
          });

          if (yearList.size > 1) {
            setComparisonState((prev) => ({
              ...prev,
              yearOptions: Array.from(yearList).sort((a, b) => a - b),
            }));
          } else {
            setSnackbarData({
              isOpen: true,
              isSuccessSeverity: false,
              text: t(
                "Consumos.No hay datos para comparar el consumo del suministro seleccionado"
              ),
            });
          }
        })
        .catch(() => {
          showCommonErrorSnackbar();
        })
        .finally(() => {
          setIsLoadingYearsOptions(false);
        });
    },
    [setComparisonState, setSnackbarData, showCommonErrorSnackbar]
  );

  useEffect(() => {
    const cupListAbortController = new AbortController();
    if (
      String(comparisonState.ownerDataCup.codeCups).trim().length > 0 &&
      ownerData &&
      userData &&
      comparisonState.yearOptions.length === 0
    ) {
      getCUPyears(
        ownerData,
        comparisonState.ownerDataCup.codeCups,
        userData.token,
        cupListAbortController.signal
      );
    }
    return () => {
      cupListAbortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCUPyears, comparisonState.ownerDataCup, ownerData]);

  useEffect(() => {
    if (canvasRef.current) {
      chart.current = getLineChart(
        environment,
        canvasRef.current,
        consumptionsList,
        isDesktopViewport,
        (elements: ActiveElement[], event: ChartEvent) => {
          handleClickLineChart(elements, chart.current as Chart);
          setSelectedDataPeriod({
            consumptions: consumptionsList.map(
              ({ consumptionList: list }) => list[elements[0].index]
            ),
            positionX: getBarPositionX(chart.current as Chart, event),
            elements,
          });
        }
      );

      return () => {
        (chart.current as Chart).destroy();
      };
    }
  }, [consumptionsList, environment, isDesktopViewport, setSelectedDataPeriod]);

  useEffect(() => {
    if (isFirstChartLoadFlag && chart.current && elements.length) {
      handleClickLineChart(elements, chart.current as Chart);
      setIsFirstChartLoadFlag(false);
    }
  }, [elements, isFirstChartLoadFlag]);

  const handleResetGraph = useCallback(() => {
    if (consumptionsList.length > 0) {
      setConsumptionsList([]);
    }
    setSelectedDataPeriod(DEFAULT_SELECTED_DATA_PERIOD_COMPARATIVE);
  }, [consumptionsList.length, setConsumptionsList, setSelectedDataPeriod]);

  const handleClickSearch = () => {
    setIsLoading(true);
    const ownerDataCupSelected = comparisonState.ownerDataCup;
    setEnvironment(ownerDataCupSelected.envirorment === "Gas" ? "Gas" : "Luz");
    const getGraphDataRequestBody: GetGraphDataRequestBody = {
      cupsId: ownerDataCupSelected.idCups.toString(),
      dateFrom: comparisonState.selectedYears.firstYear.toString(),
      dateTo: comparisonState.selectedYears.secondYear.toString(),
      hourly: false,
    };

    getGraphDataComparativeConsumptions(
      getGraphDataRequestBody,
      userData?.token || ""
    )
      .then((graphData) => {
        setConsumptionsList(formatComparativeConsumptions(graphData));
        setSelectedDataPeriod(DEFAULT_SELECTED_DATA_PERIOD_COMPARATIVE);
        setIsLoading(false);
      })
      .catch(() => {
        showCommonErrorSnackbar();
        setIsLoading(false);
      });
  };

  const handleOnChangeYear = useCallback(
    (changingFirstYear: boolean) => (event: SelectChangeEvent<string>) => {
      const newYear = parseInt(event.target.value);
      setComparisonState((prev) => {
        const firstYear = changingFirstYear
          ? newYear
          : prev.selectedYears.firstYear;
        const secondYear = !changingFirstYear
          ? newYear
          : newYear >= prev.selectedYears.secondYear
          ? NaN
          : prev.selectedYears.secondYear;
        return {
          ...prev,
          selectedYears: {
            firstYear,
            secondYear,
          },
        };
      });
    },
    [setComparisonState]
  );

  const isToCompareButtonDisabled = useMemo(
    () =>
      comparisonState.ownerDataCup.codeCups.trim().length < 1 ||
      isLoading ||
      isNaN(comparisonState.selectedYears.firstYear) ||
      isNaN(comparisonState.selectedYears.secondYear),
    [
      comparisonState.ownerDataCup.codeCups,
      comparisonState.selectedYears.firstYear,
      comparisonState.selectedYears.secondYear,
      isLoading,
    ]
  );

  const secondYearSelectorOptions = useMemo(
    () =>
      comparisonState.yearOptions.filter(
        (y) => y > comparisonState.selectedYears.firstYear
      ),
    [comparisonState.yearOptions, comparisonState.selectedYears.firstYear]
  );

  const handleSetOwnerDataCupSelected = (
    ownerDataCupSelected: OwnerDataCups
  ) => {
    setComparisonState((prev) => ({
      ...prev,
      ownerDataCup: ownerDataCupSelected,
      yearOptions: [],
      selectedYears: DEFAULT_YEARS_TO_COMPARE_CONSUMPTION,
    }));
    handleResetGraph();
  };

  return (
    <Stack component="article" rowGap={"2rem"} padding={"2rem 0"}>
      <Paper sx={{ width: "100%", borderRadius: "12px", position: "relative" }}>
        {isLoadingYearsOptions && (
          <LoadingComponentComponent
            isLoading={isLoadingYearsOptions}
            opacity={0.5}
            position={"absolute"}
            width={"100%"}
            height={"100%"}
          />
        )}
        <Stack
          padding="2rem"
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"space-between"}
          alignItems={"start"}
          gap={"2rem"}
          flexWrap={"wrap"}
        >
          <Typography component="p" variant="body1">
            {t(
              "Consumos.Seleccione el CUPS y los años para comparar los consumos"
            )}
          </Typography>
          <Stack
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            alignItems={"end"}
            gap={"2rem"}
            flexWrap={"wrap"}
          >
            <Stack width={{ xs: "100%", md: "22rem" }}>
              <ConsumptionsManagementsDeliveryAddressComponent
                ownerDataCups={ownerDataCups}
                ownerDataCupSelected={comparisonState.ownerDataCup}
                disabled={isLoading}
                setOwnerDataCupSelected={handleSetOwnerDataCupSelected}
              />
            </Stack>
            <Stack columnGap={"1rem"} rowGap={"1rem"}>
              <FormLabel>
                <Typography fontWeight={600}>
                  {t("Consumos.Seleccione años de comparación")}
                </Typography>
              </FormLabel>

              <Stack direction={"row"} rowGap={"1rem"} columnGap={"1rem"}>
                <Select
                  sx={{ width: "7rem" }}
                  value={
                    comparisonState.selectedYears.firstYear
                      ? comparisonState.selectedYears.firstYear.toString()
                      : ""
                  }
                  defaultValue=""
                  native={false}
                  onChange={handleOnChangeYear(true)}
                  disabled={
                    comparisonState.yearOptions.length === 0 || isLoading
                  }
                  aria-disabled={comparisonState.yearOptions.length === 0}
                >
                  {comparisonState.yearOptions.slice(0, -1).map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  sx={{ width: "7rem" }}
                  value={
                    comparisonState.selectedYears.secondYear
                      ? comparisonState.selectedYears.secondYear.toString()
                      : ""
                  }
                  defaultValue=""
                  onChange={handleOnChangeYear(false)}
                  disabled={
                    comparisonState.yearOptions.length === 0 ||
                    isNaN(comparisonState.selectedYears.firstYear) ||
                    isLoading
                  }
                  aria-disabled={comparisonState.yearOptions.length === 0}
                >
                  {secondYearSelectorOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Stack>
            <ButtonElement
              onClick={handleClickSearch}
              disabled={isToCompareButtonDisabled}
            >
              {t(`Comparar`)}
            </ButtonElement>
          </Stack>
        </Stack>
      </Paper>
      {!isLoading ? (
        <>
          {!!consumptionsList.length ? (
            <Paper sx={{ width: "100%", borderRadius: "12px" }}>
              <Stack component="div">
                {consumptionsList
                  .map((consumption) => consumption.totalActiveEnergy)
                  .reduce((sum, num) => sum + num, 0) > 0 ? (
                  <>
                    <Box>
                      <canvas
                        id="chart"
                        ref={canvasRef}
                        style={{ height: "600px" }}
                      />
                    </Box>
                    <Stack
                      component="div"
                      height={"auto"}
                      bgcolor={theme.palette.grey[50]}
                      border={`1px solid ${theme.palette.grey[300]}`}
                      borderRadius={"12px"}
                      margin={{ xs: "1rem", md: "3rem" }}
                      paddingY={"3rem"}
                      paddingX={{ xs: "1.5rem", md: "6rem" }}
                      position={"relative"}
                      gap={{ xs: "3rem", md: "2.5rem" }}
                    >
                      {consumptions.length ? (
                        <>
                          <Box
                            component={"div"}
                            position={"absolute"}
                            bgcolor={theme.palette.grey[50]}
                            sx={{
                              borderTop: `1px solid ${theme.palette.grey[300]}`,
                              borderLeft: `1px solid ${theme.palette.grey[300]}`,
                            }}
                            width={"20px"}
                            height={"20px"}
                            top={-11}
                            left={positionX ? positionX - 60 : 0}
                            className="arrow-graph"
                          />
                          {consumptions.map((consumption, indexNumber) => (
                            <Stack
                              key={`${consumption.invoiceDate}_${indexNumber}`}
                              borderBottom={
                                !!!indexNumber
                                  ? `1px solid ${theme.palette.grey[300]}`
                                  : ""
                              }
                              paddingBottom={
                                !!!indexNumber
                                  ? { xs: "3rem", md: "2.5rem" }
                                  : ""
                              }
                            >
                              <Box
                                display={"flex"}
                                alignItems={"center"}
                                gap={"0.5rem"}
                              >
                                <Typography
                                  component={"span"}
                                  width={"10px"}
                                  height={"10px"}
                                  borderRadius={"50px"}
                                  bgcolor={
                                    theme.palette.consumptions[
                                      !!!indexNumber ? "period1" : "period2"
                                    ].main
                                  }
                                />
                                <Typography
                                  component={"p"}
                                  variant={"subtitle1"}
                                >
                                  {`Periodo ${indexNumber + 1} (${
                                    consumption.year
                                  })`}
                                </Typography>
                              </Box>
                              <Grid
                                container
                                columnSpacing={{ xs: 12, sm: 8, md: 6 }}
                              >
                                <Grid item paddingTop="1rem">
                                  <Typography
                                    component={"p"}
                                    variant="body1"
                                    color={theme.palette.grey[500]}
                                  >
                                    {t(`Consumos.Energía Activa`)}
                                  </Typography>
                                  <Typography
                                    component={"p"}
                                    variant="subtitle1"
                                  >{`${formatNumber(
                                    consumption.totalActiveConsumption || 0
                                  )} ${
                                    environment === "Gas" ? "m3" : "kWh"
                                  }`}</Typography>
                                </Grid>
                                {periods.map((period) => {
                                  const key =
                                    `active${period}` as keyof FormattedConsumptionList;
                                  return (
                                    <Grid item key={period} paddingTop="1rem">
                                      <Typography
                                        component={"p"}
                                        variant="body1"
                                        color={theme.palette.grey[500]}
                                      >
                                        {period}
                                      </Typography>
                                      <Typography
                                        component={"p"}
                                        variant="subtitle1"
                                      >{`${formatNumber(
                                        (consumption[key] as number) || 0
                                      )} ${
                                        environment === "Gas" ? "m3" : "kWh"
                                      }`}</Typography>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Stack>
                          ))}
                        </>
                      ) : (
                        <Stack
                          display={"flex"}
                          flex-direction={"column"}
                          alignContent={"center"}
                          alignItems={"center"}
                          rowGap={2}
                        >
                          <Typography
                            component={"span"}
                            variant="h3"
                            className="t-icon-select round-icon"
                          ></Typography>
                          <Typography
                            component={"p"}
                            variant={"subtitle2"}
                            color={theme.palette.grey[700]}
                            margin={"auto"}
                          >
                            {t(
                              "Consumos.Selecciona uno de los puntos de la gráfica para comparar el consumo"
                            )}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </>
                ) : (
                  <EmptyStateComponent
                    text={
                      "Consumos.Selecciona otro periodo para mostrar consumos"
                    }
                    height={"30rem"}
                  />
                )}
              </Stack>
            </Paper>
          ) : (
            <EmptyStateComponent
              text={"Consumos.Selecciona un suministro"}
              height={"30rem"}
            />
          )}
        </>
      ) : (
        <Paper
          sx={{
            width: "100%",
            borderRadius: "12px",
            height: "30rem",
            position: "relative",
          }}
        >
          <LoadingComponentComponent isLoading={isLoading} />
        </Paper>
      )}
      <SnackbarComponent
        snackbarData={snackbarData}
        setSnackbarData={setSnackbarData}
        autoHideDuration={5000}
      />
    </Stack>
  );
};

export default ConsumptionsComparative;
